import { FoodsIcon } from "@foodpilot/foods";
import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import ForkliftIcon from "src/assets/icons/forklift-black.svg?react";
import ThreeDotsIcon from "src/assets/icons/three-dots-black.svg?react";

type ProductIngredient = {
    ingredient?: {
        name: string;
    };
    intermediateProduct?: {
        title: string;
    };
    usedQuantity?: number | null;
    quantity?: number | null;
};

type ProductRecipeProps = {
    productIngredients: ProductIngredient[];
    suppliersName: string[];
};
export const ProductRecipeSummary = (props: ProductRecipeProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { productIngredients, suppliersName } = props;

    if (productIngredients.length === 0) {
        return (
            <Typography
                variant="h3"
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                {t("pas_d_ingredient_associe_a_ce_produit")}
            </Typography>
        );
    }

    const noValue = "-";
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
            }}
        >
            <Box sx={{ width: "70%", display: "flex", flexDirection: "column" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <ThreeDotsIcon
                        style={{
                            background: theme.custom.grey[400],
                            borderRadius: "100%",
                            width: "32px",
                            height: "32px",
                            padding: "5px",
                        }}
                    />
                    <Typography
                        variant="h4"
                        sx={{ marginLeft: "12px", display: "flex", height: "32px", alignItems: "center" }}
                    >
                        {t("ingredients_du_produit_et_leur_part_dans_la_recette")}
                    </Typography>
                </Box>
                <Box
                    component="table"
                    sx={{
                        width: "100%",
                        display: "table",
                        "&>tr": {
                            display: "flex",
                        },
                        "&>tr>td": {
                            flex: 1,
                            borderBottom: `1px solid ${theme.custom.grey[500]}`,
                            padding: "16px 0px",
                        },
                        "&>tr>th": {
                            flex: 1,
                            borderBottom: `1px solid ${theme.custom.grey[500]}`,
                            padding: "16px 0px",
                            textAlign: "left",
                        },
                    }}
                >
                    <tr>
                        <th>
                            <Typography sx={{ color: theme.custom.grey[2000] }} variant="body">
                                {t("nom_de_l_ingredient")}
                            </Typography>
                        </th>
                        <th>
                            <Typography sx={{ color: theme.custom.grey[2000] }} variant="body">
                                {t("pages.product.product_form.pages.page2.ingredient_box.quantity_used")}
                            </Typography>
                        </th>
                        <th>
                            <Typography sx={{ color: theme.custom.grey[2000] }} variant="body">
                                {t("poids_dans_la_recette")}
                            </Typography>
                        </th>
                    </tr>
                    {productIngredients.map((productIngredient, index) => {
                        const { ingredient, intermediateProduct, quantity, usedQuantity } = productIngredient;
                        const title = ingredient?.name ?? intermediateProduct?.title ?? noValue;
                        return (
                            <tr key={`pi-line-recipe-${title}-${index}`}>
                                <td>
                                    <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                                        {intermediateProduct && <FoodsIcon icon="products" size={2} />}
                                        <Typography variant="body-bold">{title}</Typography>
                                    </Stack>
                                </td>
                                <td>
                                    {usedQuantity ?
                                        <Typography variant="body-medium">
                                            {usedQuantity} {"g"}
                                        </Typography>
                                    :   <Typography variant="body-medium">{noValue}</Typography>}
                                </td>
                                <td>
                                    {quantity ?
                                        <Typography variant="body-medium">
                                            {quantity} {"g"}
                                        </Typography>
                                    :   <Typography variant="body-medium">{noValue}</Typography>}
                                </td>
                            </tr>
                        );
                    })}
                </Box>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ marginLeft: "16px", marginRight: "16px" }} />
            <Box sx={{ width: "30%", display: "flex", flexDirection: "column" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <ForkliftIcon
                        style={{
                            background: theme.custom.grey[400],
                            borderRadius: "100%",
                            width: "32px",
                            height: "32px",
                            padding: "5px",
                        }}
                    />
                    <Typography
                        variant="h4"
                        sx={{ marginLeft: "12px", display: "flex", height: "32px", alignItems: "center" }}
                    >
                        {t("fournisseurs")}
                    </Typography>
                </Box>
                <ul>
                    {suppliersName.map((supplier) => (
                        <li key={`suppliers-product-${supplier}`}>{supplier}</li>
                    ))}
                </ul>
            </Box>
        </Box>
    );
};
